import { Plus, Repeat } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Switch } from "@/components/ui/misc";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { PlanStrip } from "@/features/cadence-preview";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { bodySummary } from "@/lib/cadence";
import {
  useCadences, useTenantCadencePause, useTenantCadencePaused, useUpdateCadence,
} from "@/lib/queries";
import { atLeast, type Cadence } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * Cadences (ADR-066): the follow-up policies a campaign can run. One row per cadence with its
 * first three weeks drawn a day to a cell; a row opens the cadence's own page, where admins change
 * it (a new version) and supervisors read it. A retired cadence keeps running on the campaigns
 * already on it and can be picked by no new one; nothing is ever deleted, because attempts and
 * decisions name it for good.
 */
export function CadencesPage() {
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const toast = useToast();
  const cadences = useCadences();
  const update = useUpdateCadence();
  const navigate = useNavigate();
  const [retiring, setRetiring] = React.useState<Cadence | null>(null);

  const rows = cadences.data ?? [];

  const setActive = (cadence: Cadence, active: boolean) =>
    update.mutate(
      { id: cadence.id, active },
      {
        onSuccess: () => {
          setRetiring(null);
          toast.success(active ? "Cadence restored" : "Cadence retired", cadence.name);
        },
        onError: (e) =>
          toast.error("Could not change the cadence", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  return (
    <Page>
      <PageHeader
        title="Cadences"
        description="When a lead is called next, how the calls come in cycles, and how a lead's priority moves with age. A campaign runs one from its Dialing tab."
        action={
          canManage && (
            <Button asChild variant="default">
              <Link to="/cadences/new">
                <Plus />
                New cadence
              </Link>
            </Button>
          )
        }
      />

      {canManage && user?.tenant_id != null && <PauseAll tenantId={user.tenant_id} />}

      <Card className="overflow-hidden">
        {cadences.isPending ? (
          <TableSkeleton cols={6} rows={3} />
        ) : cadences.error ? (
          <ErrorState
            error={cadences.error}
            title="Could not load the cadences"
            action={
              <Button variant="outline" onClick={() => void cadences.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Repeat}
            title="No cadences yet"
            description="A cadence says how often a lead is called and for how long. Start with the client's default: two calls a minute apart, then again in four hours, slower after two weeks."
            action={
              canManage ? (
                <Button asChild variant="default">
                  <Link to="/cadences/new">New cadence</Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Shape</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Campaigns</TableHead>
                  <TableHead>Status</TableHead>
                  {canManage && <TableHead className="w-10" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((cadence) => {
                  const current = cadence.current_version;
                  const limits = current?.body.limits;
                  return (
                    <TableRow
                      key={cadence.id}
                      className="cursor-pointer"
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest("a, button, [role=menuitem]")) return;
                        navigate(`/cadences/${cadence.id}`);
                      }}
                    >
                      <TableCell>
                        <Link to={`/cadences/${cadence.id}`} className="block text-sm font-medium text-text hover:underline">
                          {cadence.name}
                        </Link>
                        <span className="block font-mono text-xs text-text-muted">{cadence.slug}</span>
                      </TableCell>
                      <TableCell className="max-w-md text-xs text-text-muted" title={current ? bodySummary(current.body) : undefined}>
                        {current ? <PlanStrip body={current.body} /> : "No version"}
                        {current && current.unsupported_in_v1.length > 0 && (
                          <span className="mt-1 flex flex-wrap gap-1">
                            {current.unsupported_in_v1.map((flag) => (
                              <Badge key={flag} tone="warning" size="sm" title="Asked for, not run by this build yet">
                                {flag}
                              </Badge>
                            ))}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-text-muted">
                        {limits
                          ? `${limits.total_attempts ?? "—"} attempts · ${limits.expire_days_after_enrollment ? `${limits.expire_days_after_enrollment} days` : "no expiry"}`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-text-muted">
                        {current ? (
                          <>
                            v{current.version_n}
                            <span className="block text-text-subtle">{formatDateTime(current.created_at)}</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cadence.campaigns > 0 ? (
                          <Link to={`/campaigns?q=`} className="hover:underline" title="Campaigns running this cadence">
                            {cadence.campaigns}
                          </Link>
                        ) : (
                          <span className="text-text-subtle">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge tone={cadence.active ? "success" : "neutral"} size="sm">
                          {cadence.active ? "Active" : "Retired"}
                        </Badge>
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${cadence.name}`}>
                                ⋯
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => navigate(`/cadences/${cadence.id}`)}>Open</DropdownMenuItem>
                              {cadence.active ? (
                                <DropdownMenuItem destructive onSelect={() => setRetiring(cadence)}>Retire</DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onSelect={() => setActive(cadence, true)}>Restore</DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => !open && setRetiring(null)}
        title="Retire this cadence?"
        description={
          <>
            <span className="font-medium">{retiring?.name}</span> keeps running on the campaigns already on it
            {retiring && retiring.campaigns > 0 ? ` (${retiring.campaigns})` : ""}, and no new campaign can pick it.
            It can be restored.
          </>
        }
        confirmLabel="Retire"
        loading={update.isPending}
        onConfirm={() => retiring && setActive(retiring, false)}
      />
    </Page>
  );
}

/** Every cadence claim in the organisation, stopped or running. The dialer reads the flag and
 *  rewrites nothing, so resuming picks every lead up exactly where it was. */
function PauseAll({ tenantId }: { tenantId: number }) {
  const toast = useToast();
  const paused = useTenantCadencePaused(tenantId);
  const set = useTenantCadencePause();
  const on = paused.data?.paused ?? false;
  return (
    <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-border bg-surface px-4 py-3">
      <div>
        <p id="pause-all-label" className="text-sm font-medium text-text">
          Pause every cadence
        </p>
        <p className="text-xs text-text-muted">
          No cadence campaign dials while this is on. Nothing is rewritten: switching it off resumes every lead where it was.
        </p>
        {on && <Callout tone="warning" className="mt-2">Paused. Cadence campaigns are not dialing.</Callout>}
      </div>
      <Switch
        checked={on}
        disabled={paused.isPending || set.isPending}
        aria-labelledby="pause-all-label"
        onCheckedChange={(next) =>
          set.mutate(
            { tenantId, paused: next },
            {
              onSuccess: () => toast.success(next ? "Every cadence paused" : "Cadences resumed"),
              onError: (e) =>
                toast.error("Could not change the pause", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
    </div>
  );
}
